import React, {useState, useEffect} from "react";
import {Row, Col, DatePicker, Tooltip, Button} from "antd";
import moment from "moment";
import {DatePickerWithPeriodPropTypes} from "./FilterComponentPropTypes";
import {useTranslation} from "react-i18next";
import {
    SelectField,
    DateSelectCol,
    StyledRangePickerContainer,
    FilterLabel
} from "./FilterComponentStyles";
import {datePeriodOptions} from "../../../constants/constants";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";

const DatePickerWithPeriod = ({
    startDate,
    endDate,
    period,
    setStartDate,
    setEndDate,
    setPeriod,
    disabledDate,
    showPrevButton,
    showNextButton = true,
    showYesterday = true,
    showAllTime = true,
    isCustomerShiftScore = false,
    isCustomerFilter = false,
    hideTitle = false
}: DatePickerWithPeriodPropTypes) => {
    const {t} = useTranslation(["datePeriodOptions"]);
    const [dependencyCondition, setDependencyCondition] = useState(period === "choose");
    const [dates, setDates] = useState(
        startDate && endDate ? [moment(startDate), moment(endDate)] : [moment(), moment()]
    );

    useEffect(() => {
        setDependencyCondition(period === "choose");
    }, [period]);

    const onChange = value => {
        if (value === "choose") {
            setStartDate(
                moment()
                    .subtract(1, "days")
                    .format("YYYY-MM-DD")
            );
            setEndDate(moment().format("YYYY-MM-DD"));
            setPeriod(value);
            setDependencyCondition(true);
        } else if (value === "today") {
            setStartDate(moment().format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
            setPeriod(value);
            setDependencyCondition(false);
        } else if (value === "yesterday") {
            setStartDate(
                moment()
                    .subtract(1, "days")
                    .format("YYYY-MM-DD")
            );
            setEndDate(
                moment()
                    .subtract(1, "days")
                    .format("YYYY-MM-DD")
            );
            setPeriod(value);
            setDependencyCondition(false);
        } else if (value === "tomorrow") {
            setStartDate(
                moment()
                    .add(1, "days")
                    .format("YYYY-MM-DD")
            );
            setEndDate(
                moment()
                    .add(1, "days")
                    .format("YYYY-MM-DD")
            );
            setPeriod(value);
            setDependencyCondition(false);
        } else {
            setStartDate("");
            setEndDate("");
            setPeriod(value);
            setDependencyCondition(false);
        }
    };

    const nextDay = () => {
        setDates([moment(dates[0]).add(1, "days"), moment(dates[1]).add(1, "days")]);
        setStartDate(dates[0].add(1, "days").format("YYYY-MM-DD"));
        setEndDate(dates[1].add(1, "days").format("YYYY-MM-DD"));
    };

    const previousDay = () => {
        setDates([moment(dates[0]).subtract(1, "days"), moment(dates[1]).subtract(1, "days")]);
        setStartDate(dates[0].subtract(1, "days").format("YYYY-MM-DD"));
        setEndDate(dates[1].subtract(1, "days").format("YYYY-MM-DD"));
    };

    const filterByDateOptions = datePeriodOptions.filter(option => {
        if (!showYesterday && option.value === "yesterday") return false;
        if (!showNextButton && option.value === "tomorrow") return false;
        if (!showAllTime && option.value === "") return false;
        return true;
    });

    return (
        <React.Fragment>
            {!hideTitle && <FilterLabel>{t("fields.date", {ns: "common"})}</FilterLabel>}
            <Row type="flex" gutter={[8, 0]}>
                <Col
                    xs={24}
                    sm={8}
                    md={isCustomerFilter ? 16 : 8}
                    lg={isCustomerFilter ? 16 : 6}
                    xl={isCustomerShiftScore || isCustomerFilter ? 16 : 6}
                >
                    <SelectField
                        data-testid="timeRangeSelect"
                        value={period}
                        onChange={onChange}
                        style={{minWidth: !showNextButton && "270px"}}
                    >
                        {filterByDateOptions.map((obj, i) => (
                            <SelectField.Option
                                key={i}
                                data-testid="timeRangeSelect_option"
                                value={obj.value}
                            >
                                {t(obj.translation_key, {
                                    ns: "datePeriodOptions"
                                })}{" "}
                            </SelectField.Option>
                        ))}
                    </SelectField>
                </Col>
                {dependencyCondition && (
                    <DateSelectCol xs={24} sm={16} md={12} lg={14} xl={14} span={12}>
                        {dependencyCondition && showPrevButton && (
                            <Tooltip
                                title={t("prevDay", {
                                    ns: "datePeriodOptions"
                                })}
                            >
                                <Button
                                    data-testid="prevDayIcon"
                                    onClick={() => previousDay()}
                                    icon={<LeftOutlined />}
                                ></Button>
                            </Tooltip>
                        )}
                        <div
                            style={{
                                width: "100%",
                                minWidth: (!showNextButton || isCustomerFilter) && "250px",
                                maxWidth: !showYesterday && "250px"
                            }}
                        >
                            <DatePicker.RangePicker
                                panelRender={panelNode => (
                                    <StyledRangePickerContainer>
                                        {panelNode}
                                    </StyledRangePickerContainer>
                                )}
                                allowClear={false}
                                value={dates || value}
                                data-testid="timeRangePicker"
                                renderExtraFooter={() => {
                                    t("timeRangePickerPlaceholder", {ns: "common"});
                                }}
                                placeholder={[
                                    t("fields.startDate", {ns: "common"}),
                                    t("fields.endDate", {ns: "common"})
                                ]}
                                defaultValue={
                                    startDate && endDate
                                        ? [moment(startDate), moment(endDate)]
                                        : undefined
                                }
                                onChange={(_, formatString) => {
                                    setStartDate(formatString[0]);
                                    setEndDate(formatString[1]);
                                }}
                                disabledDate={current => {
                                    if (!current) return false;

                                    if (disabledDate && current < disabledDate) return true;

                                    // 6 ay fark kontrolü
                                    if (dates && dates[0]) {
                                        const maxRangeMonths = 6;
                                        const diffInMonths = Math.abs(
                                            current.diff(dates[0], "months", true)
                                        );
                                        return diffInMonths > maxRangeMonths;
                                    }

                                    return false;
                                }}
                                onCalendarChange={val => setDates(val)}
                                style={{width: "100%"}}
                            />
                        </div>
                        {dependencyCondition && showNextButton && (
                            <Tooltip
                                title={t("nextDay", {
                                    ns: "datePeriodOptions"
                                })}
                            >
                                <Button
                                    data-testid="nextDayIcon"
                                    onClick={() => nextDay()}
                                    icon={<RightOutlined />}
                                ></Button>
                            </Tooltip>
                        )}
                    </DateSelectCol>
                )}
            </Row>
        </React.Fragment>
    );
};

export default DatePickerWithPeriod;
